import { bool, node, number, oneOf, oneOfType, string } from "prop-types";
import { forwardRef, useMemo } from "react";

import styled from "styled-components/macro";
import { color, fontFamily } from "theme";
import { typographyTypes } from "theme/interpolations/fontFamily";
import lineHeight from "theme/interpolations/lineHeight";

const fontWeight = ({ $type, $bold, $fontWeight }) => {
  if ($type === typographyTypes.accent && $bold) return `font-weight: 800;`;
  if ($type === typographyTypes.accent) return `font-weight: 700;`;
  if ($type === typographyTypes.subheader && $bold) return `font-weight: 400;`;
  if ($type === typographyTypes.subheader) return `font-weight: 300;`;
  if ($bold) return `font-weight: bold;`;
  if ($fontWeight && $fontWeight !== 300) return `font-weight: ${$fontWeight};`;
  return "";
};

const Container = styled("span")`
  ${({
    $fontSize,
    $italic,
    theme: { fontSizes },
    $gutterBottom,
    $xMarginAdjust,
    $yMarginAdjust,
    $noWrap,
    $breakWord,
    $capitalize,
    $opacity,
    $textTransform,
    $letterSpacing,
  }) =>
    `font-size: ${fontSizes[$fontSize] || $fontSize};
    ${$italic ? "font-style: italic;" : ""}
    ${$gutterBottom ? "margin-bottom: 0.5em;" : ""}
    ${
      $xMarginAdjust
        ? `margin-left: ${$xMarginAdjust}px; margin-right: ${$xMarginAdjust}px; `
        : ""
    }
    ${
      $yMarginAdjust
        ? `margin-top: ${$yMarginAdjust}px; margin-bottom: ${$yMarginAdjust}px; `
        : ""
    }
    ${$noWrap ? "white-space: nowrap;" : ""}
    ${$breakWord ? "word-break: break-all;" : ""}
    ${$capitalize ? "text-transform: capitalize;" : ""}
    ${$opacity ? `opacity: ${$opacity};` : ""}
    ${$textTransform ? `text-transform: ${$textTransform};` : ""}
    ${$letterSpacing ? `letter-spacing: ${$letterSpacing};` : ""}
    `}
  ${color}
  ${fontFamily}
  ${fontWeight}
  ${lineHeight}
  transition: color 300ms, font-size 300ms;
`;

const FnrTypography = forwardRef(
  /**
   * @param {Object} param0
   * @param {'textPrimary'|'textLight'|'textSecondary'|'textSecondaryDark'|'fnDarkGreen'|'textBlack'|'textWarning'|'primary'|'secondary'|'default'|'blue'} param0.color - The key to the color needed from theme.js
   * @param {size} param0.fontSize
   * @param {number} param0.fontWeight
   * @param {boolean} param0.bold - some types default this to true. Pass false explicitly to override.
   * @param {boolean} param0.italic
   * @param {boolean} param0.noWrap
   * @param {boolean} param0.gutterBottom - adds margin to the bottom to provide a double spacing effect.
   * @param {number} param0.xMarginAdjust - adds margin to right and left to enable xAlignment with padded content
   * @param {number} param0.yMarginAdjust - adds margin to top and bottom
   * @param {boolean} param0.breakWord - breaks words to the letter
   * @param {'header'|'subheader'|'copy'|'accent'|'number'} param0.type - set the font type defaults
   * @param {number|string} param0.lineHeight
   * @param {boolean} param0.capitalize
   * @returns
   */
  (
    {
      fontSize = "md",
      italic,
      bold: boldArg,
      fontWeight: fontWeightArg = 400,
      gutterBottom,
      noWrap,
      xMarginAdjust,
      yMarginAdjust,
      breakWord,
      children,
      type,
      color: colorArg = type ? "default" : "textPrimary", // when passing in "type", use the new default.
      lineHeight: lineHeightArg,
      capitalize,
      opacity,
      textTransform,
      letterSpacing,
      ...rest
    },
    ref
  ) => {
    const bold = useMemo(() => {
      if (boldArg !== undefined) return boldArg;
      if (type === "header" || type === "accent") return true;
      return false;
    }, [boldArg, type]);
    return (
      <Container
        $color={colorArg}
        $fontSize={fontSize}
        $italic={italic}
        $bold={bold}
        $fontWeight={fontWeightArg}
        $gutterBottom={gutterBottom}
        $xMarginAdjust={xMarginAdjust}
        $yMarginAdjust={yMarginAdjust}
        $noWrap={noWrap}
        $breakWord={breakWord}
        $typography
        $type={type}
        $lineHeight={lineHeightArg}
        $capitalize={capitalize}
        $opacity={opacity}
        $textTransform={textTransform}
        $letterSpacing={letterSpacing}
        ref={ref}
        {...rest}
      >
        {children}
      </Container>
    );
  }
);

FnrTypography.propTypes = {
  color: string,
  fontSize: string,
  fontWeight: number,
  bold: bool,
  italic: bool,
  gutterBottom: bool,
  noWrap: bool,
  xMarginAdjust: number,
  yMarginAdjust: number,
  breakWord: bool,
  children: node,
  lineHeight: oneOfType([number, string]),
  capitalize: bool,
  textTransform: oneOf([
    "none",
    "capitalize",
    "uppercase",
    "lowercase",
    "inherit",
    "initial",
    "revert",
    "rever-layer",
    "unset",
  ]),
  letterSpacing: string,
  opacity: number,
  type: oneOf(Object.values(typographyTypes)),
};

export default FnrTypography;
